/**
 * 반 관리 — 통계 칸 맨 아래 [반 관리]/[내 반 정보]를 누르면 오른쪽 칸이 이 화면으로 바뀐다(오너 2026-10-10).
 * 주소 ?stats=classes(반 목록) · &class=12(반 하나). [← 현황]으로 통계로 돌아간다.
 *
 * 매니저 — 머리 탭 [반 · 부서 · 학교 · 사유·분류](주소 &set=). 반 탭은 반 만들기 · 수정 · 비활성화 · 담임 교체 ·
 *          시간표 변경 · 학생 입반, 나머지는 기준 정보 설정(SettingsPanels). 부서 탭 맨 위는 현황에서 옮겨 온 부서별 인원 막대.
 * 반에는 개강일 · 종강일이 없다 — 만든 날부터 있고, 지우지 않고 비활성화만 한다(백엔드 3단계 전달 §1). 비활성 반은 기본 목록에서 빠지고
 * 담임 · 시간표를 바꿀 수 없다.
 * 강사 — 「내 반 정보」: 본인이 오늘 담임인 반만, 반 세팅은 읽기만(요청서 §10 반 세팅 쓰기 403).
 *        본인 반의 출결 입력과 반 메모 쓰기는 연다(요청서 판 4.1 §10).
 * 아직 서버에 없는 것은 안내만 선다(stageGate) — 최근 수업·출결 7단계. 서버를 부르지 않는다.
 */
import React, { useState } from 'react';
import { AcademyButton, ActionButton, compactInputBase } from './controls';
import { ArrowLeft, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getClass, getHeadcount, listClasses, listClassSessions } from './academyApi';
import { addDays, formatMonthDay, formatSchedule, isActiveOn, isoWeekday, todayKST, weekdayLabel } from './periodDates';
import { lockedStage } from './stageGate';
import { Box, ErrorNotice, LockedNotice, SegTabs, Spinner, StateBadge, ToneBadge, useAcademyVersion, useAsync } from './ui';
import { BarList } from './stats/charts';
import StudentIdBadge from './StudentIdBadge';
import NotesPanel from './detail/NotesPanel';
import AttendanceModal from './modals/AttendanceModal';
import { SETTINGS_TABS, SettingsBody } from './SettingsPanels';
import {
  AddMembersModal, ClassActiveModal, ClassFormModal, ScheduleChangeModal, TeacherChangeModal,
} from './modals/ClassModals';

const md = (d) => `${formatMonthDay(d)}(${weekdayLabel(isoWeekday(d))})`;

/**
 * 담임 기간 · 시간표 판의 때 — 날짜로 가린다(백엔드 3단계 전달 §4-E). 끝이 비어도 앞으로 시작할 판일 수 있다
 * (뒤에 잡힌 변경 사이에 끼운 기간 · 미리 잡은 교체).
 */
function PeriodBadge({ period, today }) {
  if (period.start_on > today) return <ToneBadge tone="info">예정</ToneBadge>;
  return isActiveOn(period, today) ? <ToneBadge tone="ok">지금</ToneBadge> : null;
}

/** 부서별 재원 막대 — 현황 탭에서 옮겨 옴(오너 2026-10-10). 휴원 제외. 부서가 없는 학생은 맨 뒤 「미지정」(백엔드 6단계 전달 §4-C) */
function DepartmentBars({ version }) {
  const { getAccessToken } = useAuth();
  const r = useAsync((signal) => getHeadcount({ groupBy: 'department', signal }, getAccessToken), `dept-bars-${version}`);
  return (
    <Box title="부서별 재원" sub="휴원 제외">
      {r.loading && !r.data && <Spinner label="부서 인원 불러오는 중" />}
      {r.error && <ErrorNotice error={r.error} onRetry={r.reload} compact />}
      {r.data && <BarList ariaLabel="부서별 재원 인원" items={r.data.groups.map((g) => ({ label: g.label, value: g.count - (g.paused || 0) }))} />}
    </Box>
  );
}

function ClassList({ isTeacher, myStaffId, onSelect, onCreate, version }) {
  const { getAccessToken } = useAuth();
  const [state, setState] = useState('open');
  const [q, setQ] = useState('');
  const r = useAsync((signal) => {
    if (isTeacher && myStaffId == null) return Promise.resolve([]);
    return listClasses({ state: isTeacher ? 'open' : state, teacherStaffId: isTeacher ? myStaffId : null, signal }, getAccessToken);
  }, `class-list-${state}-${myStaffId}-${version}`);
  const rows = (r.data || []).filter((c) => !q || c.name.includes(q));
  return (
    <div className="space-y-3">
      <Box
        title={isTeacher ? '내 반' : '반'}
        sub={isTeacher ? '오늘 담임인 반' : `${rows.length}개`}
        right={!isTeacher && <AcademyButton type="button" onClick={onCreate}><Plus className="mr-0.5 inline size-3" />반 만들기</AcademyButton>}
      >
        {!isTeacher && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <SegTabs tabs={[['open', '활성'], ['closed', '비활성'], ['all', '전체']]} value={state} onChange={setState} ariaLabel="반 상태" />
            <input aria-label="반 이름 찾기" value={q} onChange={(e) => setQ(e.target.value)} placeholder="반 이름" className={`ml-auto w-36 ${compactInputBase}`} />
          </div>
        )}
        {r.loading && !r.data && <Spinner label="반 목록 불러오는 중" />}
        {r.error && <ErrorNotice error={r.error} onRetry={r.reload} compact />}
        {r.data && rows.length === 0 && <p className="py-3 text-center text-xs text-gray-500">{isTeacher ? '오늘 담임인 반이 없습니다.' : '조건에 맞는 반이 없습니다.'}</p>}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[440px] text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 font-medium">반</th>
                  <th className="py-1 font-medium">부서</th>
                  <th className="py-1 font-medium">담임</th>
                  <th className="py-1 font-medium">시간표</th>
                  {!isTeacher && <th className="py-1 text-right font-medium">인원</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.class_id} onClick={() => onSelect(c.class_id)} className="cursor-pointer border-t border-gray-100 hover:bg-primary/10">
                    <td className="py-1.5 font-medium text-gray-900">{c.name}{!c.active && <span className="ml-1"><ToneBadge tone="muted">비활성</ToneBadge></span>}</td>
                    <td className="py-1.5 text-gray-700">{c.department?.name || '—'}</td>
                    <td className="py-1.5 text-gray-700">{c.teacher?.name || '담임 없음'}</td>
                    <td className="py-1.5 text-gray-700">{formatSchedule(c.slots) || '—'}</td>
                    {!isTeacher && <td className="py-1.5 text-right text-gray-900">{c.member_count ?? '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>
    </div>
  );
}

function SessionList({ classId, className, canInput, version, onInput, onExtra }) {
  const { getAccessToken } = useAuth();
  const today = todayKST();
  const r = useAsync((signal) => listClassSessions(classId, { from: addDays(today, -13), to: today, signal }, getAccessToken), `sessions-${classId}-${version}`);
  const rows = [...(r.data || [])].reverse();
  return (
    <Box title="최근 수업 · 출결" sub="최근 2주" right={canInput && <AcademyButton type="button" onClick={onExtra}>보강·추가 회차</AcademyButton>}>
      {r.loading && !r.data && <Spinner label="회차 불러오는 중" />}
      {r.error && <ErrorNotice error={r.error} onRetry={r.reload} compact />}
      {r.data && rows.length === 0 && <p className="py-2 text-xs text-gray-500">2주 안에 수업이 없습니다.</p>}
      <ul className="divide-y divide-gray-100">
        {rows.map((s) => {
          const c = s.counts;
          const recorded = s.status === 'held' && c.unrecorded === 0;
          return (
            <li key={`${s.date}-${s.start_time}`} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
              <span className="w-20 shrink-0 font-mono text-gray-500">{md(s.date)}</span>
              <span className="text-gray-700">{s.start_time}~{s.end_time}{s.kind !== 'REGULAR' ? (s.kind === 'MAKEUP' ? ' · 보강' : ' · 추가') : ''}</span>
              {s.status === 'canceled' && <ToneBadge tone="muted">휴강</ToneBadge>}
              {s.status !== 'canceled' && recorded && <ToneBadge tone="ok">출석 {c.present} · 지각 {c.late} · 조퇴 {c.early_leave} · 결석 {c.absent}</ToneBadge>}
              {s.status !== 'canceled' && !recorded && <ToneBadge tone="warn">출결 미입력 {c.unrecorded}명</ToneBadge>}
              {s.taught_by && <span className="text-gray-400">{s.taught_by.name}</span>}
              {canInput && <AcademyButton type="button" className="ml-auto" onClick={() => onInput(s)}>{recorded || s.status === 'canceled' ? '수정' : '출결 입력'}</AcademyButton>}
            </li>
          );
        })}
      </ul>
    </Box>
  );
}

function ClassDetail({ classId, isTeacher, myStaffId, onBack, onOpenStudent, version, open }) {
  const { getAccessToken } = useAuth();
  const r = useAsync((signal) => getClass(classId, { signal }, getAccessToken), `class-${classId}-${version}`);
  const c = r.data;
  const mine = !!c && c.teacher?.staff_id === myStaffId;
  const canSet = !isTeacher;
  const canInput = !isTeacher || mine;
  const today = todayKST();
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ActionButton onClick={onBack} icon={ArrowLeft}>{isTeacher ? '내 반 목록' : '반 목록'}</ActionButton>
        {c && <h4 className="text-base font-semibold text-gray-900">{c.name}</h4>}
        {c && !c.active && <ToneBadge tone="muted">비활성</ToneBadge>}
        {canSet && c && (
          <span className="ml-auto flex flex-wrap gap-1.5">
            <AcademyButton type="button" onClick={() => open({ type: 'edit', cls: c })}>수정</AcademyButton>
            {c.active
              ? <AcademyButton type="button" onClick={() => open({ type: 'active', cls: c })} variant="danger">비활성화</AcademyButton>
              : <AcademyButton type="button" onClick={() => open({ type: 'active', cls: c })}>다시 활성화</AcademyButton>}
          </span>
        )}
      </div>
      {r.loading && !c && <Spinner label="반 불러오는 중" />}
      {r.error && <ErrorNotice error={r.error} onRetry={r.reload} />}
      {c && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {[
              ['부서', c.department?.name || '—'],
              // 개강일 · 종강일은 없다 — 활성이면 만든 날, 비활성이면 비활성화한 날(백엔드 3단계 전달 §4-B)
              ['상태', c.active ? `활성 · ${c.created_on ? `${formatMonthDay(c.created_on)} 만듦` : ''}` : `비활성 ${formatMonthDay(String(c.inactive_at || '').slice(0, 10))}`],
              ['담임', c.teacher?.name || '담임 없음'],
              ['재원', `${c.members.length}명`],
            ].map(([k, v]) => (
              <div key={k} className="min-w-0 rounded-2xl border border-gray-200 px-2.5 py-2">
                <p className="text-xs text-gray-500">{k}</p>
                <p className="truncate text-sm font-semibold text-gray-900" title={v}>{v}</p>
              </div>
            ))}
          </div>
          {canSet && !c.active && (
            <p role="status" className="rounded-2xl bg-gray-50 px-3 py-2 text-xs text-gray-600">비활성 반</p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <Box title="담임 기간" right={canSet && c.active && <AcademyButton type="button" onClick={() => open({ type: 'teacher', cls: c })}>담임 교체</AcademyButton>}>
              <ul className="divide-y divide-gray-100">
                {[...c.teachers].reverse().map((t) => (
                  <li key={t.id ?? t.start_on} className="flex items-center gap-2 py-1.5 text-xs">
                    <span className="font-medium text-gray-900">{t.name}</span>
                    <span className="font-mono text-gray-500">{t.start_on} ~ {t.end_on || ''}</span>
                    <PeriodBadge period={t} today={today} />
                  </li>
                ))}
              </ul>
            </Box>
            <Box title="시간표" right={canSet && c.active && <AcademyButton type="button" onClick={() => open({ type: 'schedule', cls: c })}>시간표 변경</AcademyButton>}>
              <ul className="divide-y divide-gray-100">
                {[...c.schedules].reverse().map((sc) => (
                  <li key={sc.id ?? sc.start_on} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
                    <span className="text-gray-900">{formatSchedule(sc.slots) || '—'}</span>
                    <span className="font-mono text-gray-500">{sc.start_on} ~ {sc.end_on || ''}</span>
                    <PeriodBadge period={sc} today={today} />
                  </li>
                ))}
              </ul>
            </Box>
          </div>
          <Box
            title={`학생 ${c.members.length}명`}
            right={canSet && c.active && <AcademyButton type="button" onClick={() => open({ type: 'members', cls: c })}>학생 입반</AcademyButton>}
          >
            {c.members.length === 0 ? <p className="py-2 text-xs text-gray-500">편성된 학생이 없습니다.</p> : (
              <div className="overflow-x-auto">
                <table aria-label={`${c.name} 학생 목록`} className="w-full text-xs">
                  <colgroup>
                    <col className="w-10 md:w-12" />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <thead className="bg-gray-50 text-gray-500">
                    <tr><th className="rounded-l-xl p-2 text-center font-medium">SID</th><th className="px-3 py-2 text-left font-medium">학생</th><th className="px-3 py-2 text-left font-medium">입반일</th><th className="rounded-r-xl px-3 py-2 text-right font-medium">상태</th></tr>
                  </thead>
                  <tbody>
                    {c.members.map((m) => (
                      <tr key={m.student_id} className="border-b border-gray-100 transition-colors hover:bg-primary/10">
                        <td className="p-2 text-center"><StudentIdBadge id={m.student_id} blocked={m.is_blocked} /></td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {onOpenStudent && m.student_id ? <button type="button" onClick={() => onOpenStudent(m.student_id)} className="min-h-8 rounded-lg text-left font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">{m.student_name}</button> : m.student_name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-500">{m.start_on || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-right"><StateBadge row={{ state: m.state }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Box>
          {lockedStage('attendance')
            ? <Box title="최근 수업 · 출결"><LockedNotice feature="attendance" what="최근 수업 · 출결 입력 · 보강·추가 회차" compact /></Box>
            : (
              <SessionList classId={c.class_id} className={c.name} canInput={canInput} version={version}
                onInput={(s) => open({ type: 'attendance', cls: c, session: s })} onExtra={() => open({ type: 'attendance', cls: c, extra: true })} />
            )}
          {/* 강사는 오늘 담임인 활성 반에만 반 메모를 쓰고(서버 403 out_of_scope), 본인 메모만 고치고 지운다(백엔드 8단계 전달 §4-D · §4-G) */}
          <NotesPanel
            target={{ classId: c.class_id }}
            title="반 메모"
            canWrite={!isTeacher || (mine && c.active)}
            canEdit={(n) => !isTeacher || (myStaffId != null && n.author_staff_id === myStaffId)}
          />
        </>
      )}
    </div>
  );
}

function ClassAdminPanel({ isTeacher = false, myStaffId = null, classId = null, settingsTab = 'classes', onSettingsTab, onSelectClass, onBack, onOpenStudent }) {
  const tab = !isTeacher && SETTINGS_TABS.some(([k]) => k === settingsTab) ? settingsTab : 'classes';
  const version = useAcademyVersion();
  const [modal, setModal] = useState(null);
  const close = () => setModal(null);
  return (
    <div className="flex size-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3">
        <ActionButton onClick={onBack} icon={ArrowLeft}>{isTeacher ? '내 반 현황' : '학원 현황'}</ActionButton>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{isTeacher ? '내 반 정보' : '반 · 기준 설정'}</h3>
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {!isTeacher && <div className="flex flex-wrap"><SegTabs tabs={SETTINGS_TABS} value={tab} onChange={onSettingsTab} ariaLabel="설정 탭" /></div>}
        {tab === 'classes' && (classId
          ? <ClassDetail classId={classId} isTeacher={isTeacher} myStaffId={myStaffId} onBack={() => onSelectClass(null)} onOpenStudent={onOpenStudent} version={version} open={setModal} />
          : <ClassList isTeacher={isTeacher} myStaffId={myStaffId} onSelect={onSelectClass} onCreate={() => setModal({ type: 'create' })} version={version} />)}
        {tab === 'departments' && <DepartmentBars version={version} />}
        {tab !== 'classes' && <SettingsBody tab={tab} />}
      </div>
      {modal?.type === 'create' && <ClassFormModal onClose={close} onCreated={(id) => onSelectClass(id)} />}
      {modal?.type === 'edit' && <ClassFormModal cls={modal.cls} onClose={close} />}
      {modal?.type === 'active' && <ClassActiveModal cls={modal.cls} onClose={close} />}
      {modal?.type === 'teacher' && <TeacherChangeModal cls={modal.cls} onClose={close} />}
      {modal?.type === 'schedule' && <ScheduleChangeModal cls={modal.cls} onClose={close} />}
      {modal?.type === 'members' && <AddMembersModal cls={modal.cls} memberIds={modal.cls.members.map((m) => m.student_id)} onClose={close} />}
      {modal?.type === 'attendance' && (
        <AttendanceModal classId={modal.cls.class_id} className={modal.cls.name} date={modal.session?.date} startTime={modal.session?.start_time} extra={!!modal.extra} onClose={close} />
      )}
    </div>
  );
}

export default ClassAdminPanel;
